import { supabase } from '../lib/supabaseClient';
import type { MaterialSiteLinkWithSite } from '../types/materialSiteLinks';

/**
 * material_site_links を取得
 */
export async function fetchMaterialSiteLinks(
  materialId: string
): Promise<MaterialSiteLinkWithSite[]> {
  try {
    console.log('🔗 おすすめサイトを取得中:', materialId);

    const { data, error } = await supabase
      .from('material_site_links')
      .select(`
        id,
        material_id,
        site_id,
        position,
        is_primary,
        source,
        created_at,
        sites (
          id,
          name,
          base_url,
          description,
          subject,
          grade
        )
      `)
      .eq('material_id', materialId)
      .order('position', { ascending: true });

    if (error) {
      console.error('❌ おすすめサイト取得エラー:', error.message);
      return [];
    }

    const results: MaterialSiteLinkWithSite[] = (data || [])
      .filter((item: any) => item.sites)
      .map((item: any) => ({
        id: item.id,
        materialId: item.material_id,
        siteId: item.site_id,
        position: item.position,
        isPrimary: item.is_primary,
        source: item.source,
        createdAt: item.created_at,
        site: {
          id: item.sites.id,
          name: item.sites.name,
          baseUrl: item.sites.base_url,
          description: item.sites.description,
          subject: item.sites.subject,
          grade: item.sites.grade,
        },
      }));

    console.log('✅ おすすめサイト取得成功:', results.length, '件');
    return results;
  } catch (error) {
    console.error('❌ おすすめサイト取得例外:', error);
    return [];
  }
}

/**
 * おすすめサイトの件数を取得
 */
export async function countMaterialSiteLinks(materialId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('material_site_links')
      .select('*', { count: 'exact', head: true })
      .eq('material_id', materialId);

    if (error) {
      console.error('❌ おすすめサイト件数取得エラー:', error.message);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('❌ おすすめサイト件数取得例外:', error);
    return 0;
  }
}

/**
 * おすすめサイトを追加
 */
export async function createMaterialSiteLink(payload: {
  materialId: string;
  siteId: string;
  isPrimary?: boolean;
  position?: number;
  source?: string;
}): Promise<void> {
  try {
    console.log('➕ おすすめサイトを追加中:', payload);

    const { error } = await supabase
      .from('material_site_links')
      .insert({
        material_id: payload.materialId,
        site_id: payload.siteId,
        is_primary: payload.isPrimary ?? false,
        position: payload.position ?? 0,
        source: payload.source ?? 'manual',
      });

    if (error) {
      console.error('❌ おすすめサイト追加エラー:', error.message);
      throw error;
    }

    console.log('✅ おすすめサイト追加成功');
  } catch (error) {
    console.error('❌ おすすめサイト追加例外:', error);
    throw error;
  }
}

/**
 * おすすめサイトを更新
 */
export async function updateMaterialSiteLink(
  id: string,
  patch: {
    isPrimary?: boolean;
    position?: number;
  }
): Promise<void> {
  try {
    console.log('🔄 おすすめサイトを更新中:', id, patch);

    const update: Record<string, any> = {
      updated_at: new Date().toISOString(),
    };

    if (typeof patch.isPrimary === 'boolean') {
      update.is_primary = patch.isPrimary;
    }

    if (typeof patch.position === 'number' && !Number.isNaN(patch.position)) {
      update.position = patch.position;
    }

    const { error } = await supabase
      .from('material_site_links')
      .update(update)
      .eq('id', id);

    if (error) {
      console.error('❌ おすすめサイト更新エラー:', error.message);
      throw error;
    }

    console.log('✅ おすすめサイト更新成功');
  } catch (error) {
    console.error('❌ おすすめサイト更新例外:', error);
    throw error;
  }
}

/**
 * おすすめサイトを削除
 */
export async function deleteMaterialSiteLink(id: string): Promise<void> {
  try {
    console.log('🗑️ おすすめサイトを削除中:', id);

    const { error } = await supabase
      .from('material_site_links')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('❌ おすすめサイト削除エラー:', error.message);
      throw error;
    }

    console.log('✅ おすすめサイト削除成功');
  } catch (error) {
    console.error('❌ おすすめサイト削除例外:', error);
    throw error;
  }
}
